import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { GraduationCapIcon, PlusIcon } from "@phosphor-icons/react";
import type { Education } from "@reactive-resume/schema";

import { EducationCard } from "./education-card";

type Props = {
  items: Education[];
  onAdd: () => void;
  onEdit: (item: Education) => void;
  onDelete: (id: string) => void;
  onReorder: (active: string, over: string) => void;
};

export const EducationList = ({ items, onAdd, onEdit, onDelete, onReorder }: Props) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const dragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  };
  if (!items.length)
    return (
      <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30 px-5 py-9 text-center">
        <span className="mx-auto grid size-11 place-items-center rounded-xl bg-white text-indigo-600 shadow-sm">
          <GraduationCapIcon size={23} weight="duotone" />
        </span>
        <h3 className="mt-3 text-sm font-bold text-indigo-950">Aucune formation ajoutée</h3>
        <p className="mx-auto mt-1 max-w-sm text-[11px] leading-5 text-slate-500">
          Ajoutez d’abord la formation la plus récente ou la plus pertinente.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="mt-4 inline-flex min-h-10 items-center gap-2 rounded-xl bg-indigo-700 px-4 text-xs font-bold text-white hover:bg-indigo-800"
        >
          <PlusIcon size={15} weight="bold" /> Ajouter une formation
        </button>
      </div>
    );
  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-xs font-bold text-indigo-950 sm:text-sm">Vos formations</h3>
          <p className="mt-0.5 text-[10px] text-slate-500">Réorganisez-les par glisser-déposer.</p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-9 items-center gap-1.5 rounded-xl border border-indigo-200 px-3 text-[10px] font-bold text-indigo-700 hover:bg-indigo-50"
        >
          <PlusIcon size={14} weight="bold" /> Ajouter
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToParentElement]}
        onDragEnd={dragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2.5">
            {items.map((item) => (
              <EducationCard key={item.id} item={item} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};
